export enum LoanBorrowerType {
  EMPLOYEE = 'employee',
  OTHER = 'other',
}

export enum LoanAgreementStatus {
  ACTIVE = 'active',
  SETTLED = 'settled',
  BAD_DEBT = 'bad_debt',
}

export enum LoanPlanType {
  EQUAL = 'equal',
  CUSTOM = 'custom',
}

export enum LoanInstallmentFrequency {
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
}

export enum LoanInstallmentStatus {
  PAID = 'paid',
  PARTIAL = 'partial',
  OVERDUE = 'overdue',
  UPCOMING = 'upcoming',
}

// Must match LOAN_APPROVERS on the server, which rejects anything else.
export const LOAN_APPROVERS = ['Priyantha Basnayaka', 'Sithum Basnayaka'];

export const LOAN_STATUS_TABS = [
  { value: LoanAgreementStatus.ACTIVE, label: 'Active' },
  { value: LoanAgreementStatus.SETTLED, label: 'Settled' },
  { value: LoanAgreementStatus.BAD_DEBT, label: 'Bad Debt' },
];

export const LOAN_BORROWER_TYPE_OPTIONS = [
  { value: LoanBorrowerType.EMPLOYEE, label: 'Employee' },
  { value: LoanBorrowerType.OTHER, label: 'Other' },
];

export const LOAN_FREQUENCY_OPTIONS = [
  { value: LoanInstallmentFrequency.MONTHLY, label: 'Monthly' },
  { value: LoanInstallmentFrequency.WEEKLY, label: 'Weekly' },
];

// Sri Lankan NIC (old: 9 digits + V/X, new: 12 digits) and phone numbers.
// Same patterns the server validates against.
export const NIC_PATTERN = /^(\d{9}[VvXx]|\d{12})$/;
export const PHONE_PATTERN = /^(?:\+94|94|0)\d{9}$/;

export type ILoanTableFilters = {
  generalSearch: string;
  borrowerType: LoanBorrowerType | 'all';
  overdue: boolean;
};

export type ILoanTableFilterValue = string | boolean;

export type ILoanTableSort = {
  sortBy: string;
  order: 'asc' | 'desc';
};

export interface ILoanUserRef {
  _id: string;
  firstName: string;
  lastName?: string;
  email?: string;
}

export interface ILoanEmployeeRef {
  _id: string;
  firstName: string;
  lastName?: string;
  employeeNumber: string;
  nicNumber?: string;
  phoneNumber?: string;
  status?: string;
}

export interface ILoanInstallment {
  dueDate: string;
  amountInCents: number;
}

export interface ILoanInstallmentSummary extends ILoanInstallment {
  number: number;
  paidInCents: number;
  remainingInCents: number;
  status: LoanInstallmentStatus;
}

export interface ILoanSummary {
  capitalInCents: number;
  paidInCents: number;
  remainingInCents: number;
  progressPercent: number;
  overdueAmountInCents: number;
  isOverdue: boolean;
  nextDue: { dueDate: string; amountInCents: number } | null;
  installments: ILoanInstallmentSummary[];
}

export interface ILoanPayment {
  _id: string;
  loanAgreement: string;
  amountInCents: number;
  paymentDate: string;
  note?: string;
  createdBy: ILoanUserRef;
  updatedBy: ILoanUserRef;
  createdAt: string;
  updatedAt: string;
}

export interface ILoanAgreementListItem {
  _id: string;
  borrowerType: LoanBorrowerType;
  employee?: ILoanEmployeeRef;
  displayName: string;
  borrowerName?: string;
  nicNumber?: string;
  phoneNumber?: string;
  capitalInCents: number;
  paidInCents: number;
  remainingInCents: number;
  overdueAmountInCents: number;
  progressPercent: number;
  loanPaidDate: string;
  settlementDate: string;
  settledAt?: string;
  approvedBy: string;
  status: LoanAgreementStatus;
  badDebt?: { reason: string; markedAt: string };
  createdAt: string;
}

export interface ILoanAgreementItem {
  _id: string;
  borrowerType: LoanBorrowerType;
  employee?: ILoanEmployeeRef;
  borrowerName?: string;
  nicNumber?: string;
  phoneNumber?: string;
  capitalInCents: number;
  paidInCents: number;
  loanPaidDate: string;
  planType: LoanPlanType;
  frequency?: LoanInstallmentFrequency;
  installmentAmountInCents?: number;
  schedule: ILoanInstallment[];
  settlementDate: string;
  approvedBy: string;
  status: LoanAgreementStatus;
  settledAt?: string;
  badDebt?: { reason: string; markedAt: string; markedBy: ILoanUserRef };
  badDebtReversal?: { reason: string; reversedAt: string; reversedBy: ILoanUserRef };
  badDebtOverride?: {
    acknowledgedAt: string;
    acknowledgedBy: ILoanUserRef;
    matchedAgreements: string[];
  };
  createdBy: ILoanUserRef;
  updatedBy: ILoanUserRef;
  createdAt: string;
  updatedAt: string;
  payments: ILoanPayment[];
  summary: ILoanSummary;
}

export type BadDebtMatchField = 'employee' | 'nic' | 'phone' | 'name';

export interface IBadDebtMatch {
  _id: string;
  borrowerType: LoanBorrowerType;
  borrowerName: string;
  employeeNumber?: string;
  nicNumber?: string;
  phoneNumber?: string;
  capitalInCents: number;
  paidInCents: number;
  outstandingInCents: number;
  badDebt: { reason: string; markedAt: string };
  matchedOn: BadDebtMatchField[];
}

export interface ILoanPlanPayload {
  planType: LoanPlanType;
  frequency?: LoanInstallmentFrequency;
  installmentAmountInCents?: number;
  firstDueDate?: string;
  schedule?: { dueDate: string; amountInCents: number }[];
}

export interface ILoanBorrowerPayload {
  borrowerType: LoanBorrowerType;
  employee?: string;
  borrowerName?: string;
  nicNumber?: string;
  phoneNumber?: string;
}

export type ICreateLoanAgreementPayload = ILoanBorrowerPayload &
  ILoanPlanPayload & {
    capitalInCents: number;
    loanPaidDate: string;
    approvedBy: string;
    acknowledgeBadDebt?: boolean;
  };

export type IUpdateLoanAgreementPayload = Partial<Omit<ILoanBorrowerPayload, 'borrowerType'>> & {
  capitalInCents?: number;
  plan?: ILoanPlanPayload;
  acknowledgeBadDebt?: boolean;
};

export interface ILoanPaymentPayload {
  paymentDate: string;
  amountInCents: number;
  note?: string;
}

export function loanBorrowerName(loan: {
  borrowerType: LoanBorrowerType;
  employee?: { firstName?: string; lastName?: string };
  borrowerName?: string;
}): string {
  if (loan.borrowerType === LoanBorrowerType.EMPLOYEE) {
    return [loan.employee?.firstName, loan.employee?.lastName].filter(Boolean).join(' ');
  }
  return loan.borrowerName ?? '';
}

export function loanBorrowerIdLine(loan: {
  borrowerType: LoanBorrowerType;
  employee?: { employeeNumber?: string };
  nicNumber?: string;
  phoneNumber?: string;
}): string {
  if (loan.borrowerType === LoanBorrowerType.EMPLOYEE) {
    return `Employee ID: ${loan.employee?.employeeNumber ?? '-'}`;
  }
  const parts = [
    loan.nicNumber && `NIC: ${loan.nicNumber}`,
    loan.phoneNumber && `Phone: ${loan.phoneNumber}`,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'No NIC or phone on record';
}
